import { useEffect, useState, useTransition } from 'react';

import { GrowthbookFlag } from '../constants/feature-flags';
import { useFeatureFlagIsOn } from '../hooks/feature-flags';
import type { NavigationOptions } from '../providers/RouterProvider';
import { captureMessage } from '../utils/error-reporter';

export interface BaseRouter {
  push: (path: string, options?: { scroll?: boolean }) => void;
  replace: (path: string, options?: { scroll?: boolean }) => void;
}

export const useAppRouter = (baseRouter: BaseRouter) => {
  const isClientSideNavigationEnabled = useFeatureFlagIsOn(GrowthbookFlag.ClientSideNavigation);

  const [isNavigating, startTransition] = useTransition();
  const [isHardNavigating, setIsHardNavigating] = useState(false);

  useEffect(() => {
    if (isClientSideNavigationEnabled || !isHardNavigating) return;

    const handler = () => setIsHardNavigating(false);
    window.addEventListener('pagehide', handler);

    return () => window.removeEventListener('pagehide', handler);
  }, [isClientSideNavigationEnabled, isHardNavigating]);

  const navigateWithFallback = (
    method: 'push' | 'replace',
    path: string,
    scroll: boolean = false
  ) => {
    try {
      baseRouter[method](path, { scroll });
    } catch (error) {
      captureMessage(`Router ${method} for ${path} failed with error: ${error}`);
      window.location.href = path;
    }
  };

  const navigate = (
    method: 'push' | 'replace',
    path: string,
    { scroll, withoutNavigationState }: NavigationOptions = {}
  ) => {
    if (!isClientSideNavigationEnabled) {
      if (!withoutNavigationState) {
        setIsHardNavigating(true);
      }
      window.location.href = path;

      return;
    }

    if (withoutNavigationState) {
      navigateWithFallback(method, path, scroll);
    } else {
      startTransition(() => navigateWithFallback(method, path, scroll));
    }
  };

  const push = (path: string, options?: NavigationOptions) => navigate('push', path, options);
  const replace = (path: string, options?: NavigationOptions) => navigate('replace', path, options);

  return {
    push,
    replace,
    isNavigating: isNavigating || isHardNavigating,
  };
};
